"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronRight, Sparkles } from "lucide-react";

/**
 * 3D-Bühne im Hintergrund + messerscharfe Inline-Expansion (StretchText) im Vordergrund:
 * Ein Fachbegriff ("FDM") klappt im Satz live mit leuchtendem Erklär-Chip auf!
 */
export default function Preview() {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setExpanded((v) => !v), 2500);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-4 select-none"
      style={{ perspective: "900px" }}
    >
      {/* 1. Rose-Spotlight */}
      <motion.div
        animate={{ scale: [1, 1.22, 1], opacity: [0.45, 0.85, 0.45] }}
        transition={{ duration: 4.5, repeat: Infinity }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-40 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-rose-500/30 blur-3xl"
      />

      {/* 2. Gekipptes 3D-Bodenraster */}
      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(244,63,94,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(244,63,94,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* 3. Gestochen scharfe Lesekarte */}
      <motion.div
        animate={{ y: expanded ? -3 : 2 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        className={`relative z-10 flex w-60 flex-col gap-2 rounded-2xl border bg-white p-3.5 transition-all duration-500 antialiased dark:bg-zinc-900 ${
          expanded
            ? "border-rose-400/80 shadow-[0_18px_36px_-6px_rgba(244,63,94,0.5)]"
            : "border-black/15 shadow-lg dark:border-white/15"
        }`}
      >
        <div className="flex flex-wrap items-center gap-1.5 font-sans text-xs font-medium text-neutral-800 dark:text-neutral-200">
          <span>3D-Druck per</span>

          {/* Interaktiver Begriff */}
          <span className="inline-flex items-center gap-0.5 rounded-md bg-rose-500/15 px-1.5 py-0.5 font-extrabold text-rose-600 dark:text-rose-400">
            <span>FDM</span>
            <ChevronRight
              className={`h-3 w-3 transition-transform duration-300 ${
                expanded ? "rotate-90" : ""
              }`}
            />
          </span>

          <span>im Büro.</span>
        </div>

        {/* Inline aufgefaltete Erklärung */}
        <motion.div
          animate={{
            height: expanded ? "auto" : 0,
            opacity: expanded ? 1 : 0,
          }}
          className="overflow-hidden"
        >
          <div className="flex items-center gap-1.5 rounded-xl border border-rose-400/40 bg-rose-500/10 px-2.5 py-1.5 text-[11px] font-bold text-rose-700 dark:text-rose-300">
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-rose-500" />
            <span>= Schmelzschichtung (Schicht für Schicht)</span>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
